/**
 * Outil « Repère de l'espace » — logique pure (Tle → prépa).
 *
 * Coordonnées entières saisies, tout le reste EXACT (fractions, racines
 * c√r) : vecteur AB, longueur, milieu ; colinéarité (v = k u) et coplanarité
 * (w = a u + b v résolu sur deux coordonnées, vérifié sur la troisième) ;
 * plan de vecteur normal n passant par A (ax + by + cz + d = 0), droite
 * x = x_B + αt… , position relative et point d'intersection, projeté
 * orthogonal et distance d'un point au plan. Notations des cartes « Vecteurs
 * de l'espace : colinéarité, coplanarité », « Représentation paramétrique
 * d'une droite », « Équation cartésienne d'un plan », « Projeté orthogonal et
 * distance », « Distance d'un point à un plan et à une droite ».
 */

import { add, div, frac, isZero, mul, sub, type Frac } from '../../ui/exact';
import {
  fracText,
  overSqrt,
  qadd,
  qscale,
  sqrtFrac,
  surdText,
  toQ,
  vecText,
  type IV,
  type QV,
} from '../gram-schmidt/gram';

export type { IV, QV };
export { vecText, fracText };

export const COORD_MIN = -4;
export const COORD_MAX = 4;

const m = (v: number) => String(v).replace('-', '−');
export const idot = (a: IV, b: IV) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const icross = (a: IV, b: IV): IV => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const isub = (a: IV, b: IV): IV => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const n2 = (v: IV) => idot(v, v);
export const isNull = (v: IV) => v.every((c) => c === 0);
export const collinear = (a: IV, b: IV) => isNull(icross(a, b));
export const det3 = (u: IV, v: IV, w: IV) => idot(icross(u, v), w);
const COORDS = ['x', 'y', 'z'] as const;

/* ---------- points ---------- */

/** « √29 », « 3√2 », « 5 » : racine d'un entier, simplifiée */
export const rootText = (n: number) => surdText(sqrtFrac(frac(n)));

export interface PointsInfo {
  AB: IV;
  /** AB² */
  sq: number;
  length: string;
  middle: QV;
}

export function points(A: IV, B: IV): PointsInfo {
  const AB = isub(B, A);
  const sq = n2(AB);
  return {
    AB,
    sq,
    length: rootText(sq),
    middle: [frac(A[0] + B[0], 2), frac(A[1] + B[1], 2), frac(A[2] + B[2], 2)],
  };
}

/** « AB = √(2² + (−1)² + 2²) = √9 = 3 » */
export function lengthText(AB: IV, name = 'AB'): string {
  const sq = n2(AB);
  const terms = AB.map((c) => (c < 0 ? `(${m(c)})²` : `${c}²`)).join(' + ');
  const root = rootText(sq);
  return `${name} = √(${terms}) = √${sq}${root === `√${sq}` ? '' : ` = ${root}`}`;
}

/* ---------- colinéarité, coplanarité ---------- */

export type Colin =
  | { kind: 'null'; which: 'u' | 'v' }
  | { kind: 'yes'; k: Frac }
  | { kind: 'no'; why: string };

/** u et v colinéaires ? (u ≠ 0 : v = k u) */
export function colinearity(u: IV, v: IV): Colin {
  if (isNull(u)) return { kind: 'null', which: 'u' };
  if (isNull(v)) return { kind: 'null', which: 'v' };
  const i = u.findIndex((c) => c !== 0);
  const k = frac(v[i]!, u[i]!);
  for (let j = 0; j < 3; j++) {
    if (isZero(sub(frac(v[j]!), mul(k, frac(u[j]!))))) continue;
    const c = COORDS;
    const why =
      u[j] === 0
        ? `la coordonnée ${c[j]} est nulle pour u mais vaut ${m(v[j]!)} pour v`
        : `${c[i]!} donne k = ${fracText(k)}, mais ${c[j]!} donne ${fracText(frac(v[j]!, u[j]!))}`;
    return { kind: 'no', why };
  }
  return { kind: 'yes', k };
}

export type Copl =
  | { kind: 'colinear' }
  | { kind: 'yes'; a: Frac; b: Frac; rows: [number, number] }
  | { kind: 'no'; a: Frac; b: Frac; rows: [number, number]; third: number; got: Frac };

/**
 * w = a u + b v ? (u, v non colinéaires) : a et b par les deux coordonnées
 * dont le déterminant 2 × 2 est non nul (Cramer), vérification sur la troisième.
 */
export function coplanarity(u: IV, v: IV, w: IV): Copl {
  if (collinear(u, v)) return { kind: 'colinear' };
  const pairs: [number, number][] = [
    [0, 1],
    [0, 2],
    [1, 2],
  ];
  const [i, j] = pairs.find(([p, q]) => u[p]! * v[q]! - u[q]! * v[p]! !== 0)!;
  const D = u[i]! * v[j]! - u[j]! * v[i]!;
  const a = frac(w[i]! * v[j]! - w[j]! * v[i]!, D);
  const b = frac(u[i]! * w[j]! - u[j]! * w[i]!, D);
  const k = 3 - i - j;
  const got = add(mul(a, frac(u[k]!)), mul(b, frac(v[k]!)));
  if (isZero(sub(got, frac(w[k]!)))) return { kind: 'yes', a, b, rows: [i, j] };
  return { kind: 'no', a, b, rows: [i, j], third: k, got };
}

/** « 2u − v », « u + 1/2 v », « −3v », « 0 » */
export function comboText(a: Frac, b: Frac, U = 'u', V = 'v'): string {
  const term = (c: Frac, name: string, first: boolean) => {
    if (isZero(c)) return '';
    const neg = c.p < 0;
    const abs = frac(Math.abs(c.p), c.q);
    const coef = abs.p === 1 && abs.q === 1 ? '' : abs.q === 1 ? `${abs.p}` : `${fracText(abs)} `;
    return first ? `${neg ? '−' : ''}${coef}${name}` : ` ${neg ? '−' : '+'} ${coef}${name}`;
  };
  const s = `${term(a, U, true)}${term(b, V, isZero(a))}`;
  return s || '0';
}

/* ---------- plan et droite ---------- */

export interface Plane {
  n: IV;
  A: IV;
}
export interface Line {
  B: IV;
  u: IV;
}

/** d tel que ax + by + cz + d = 0 passe par A */
export const planeD = (P: Plane) => -idot(P.n, P.A);

/** « 2x − y + 3z − 5 = 0 », « x + 2y = 0 », « z − 1 = 0 » */
export function planeText(P: Plane): string {
  const d = planeD(P);
  const parts: string[] = [];
  P.n.forEach((c, i) => {
    if (c === 0) return;
    const abs = Math.abs(c) === 1 ? COORDS[i]! : `${Math.abs(c)}${COORDS[i]}`;
    parts.push(parts.length ? `${c < 0 ? '−' : '+'} ${abs}` : `${c < 0 ? '−' : ''}${abs}`);
  });
  if (d !== 0) parts.push(`${d < 0 ? '−' : '+'} ${Math.abs(d)}`);
  return `${parts.join(' ')} = 0`;
}

/** « x = 1 + 2t », « y = t », « z = 2 − t », « x = 3 » */
export function paramLines(L: Line, t = 't'): string[] {
  return COORDS.map((c, i) => {
    const b = L.B[i]!;
    const a = L.u[i]!;
    const at = a === 0 ? '' : Math.abs(a) === 1 ? t : `${Math.abs(a)}${t}`;
    if (a === 0) return `${c} = ${m(b)}`;
    if (b === 0) return `${c} = ${a < 0 ? '−' : ''}${at}`;
    return `${c} = ${m(b)} ${a < 0 ? '−' : '+'} ${at}`;
  });
}

export type Position =
  | { kind: 'secante'; t: Frac; H: QV; orth: boolean }
  | { kind: 'parallele' }
  | { kind: 'incluse' };

/** position de la droite par rapport au plan ; t : paramètre du point d'intersection */
export function position(P: Plane, L: Line): Position {
  const nu = idot(P.n, L.u);
  const val = idot(P.n, L.B) + planeD(P);
  if (nu === 0) return val === 0 ? { kind: 'incluse' } : { kind: 'parallele' };
  const t = frac(-val, nu);
  return { kind: 'secante', t, H: qadd(toQ(L.B), qscale(t, toQ(L.u))), orth: collinear(P.n, L.u) };
}

export interface Projection {
  /** paramètre sur la droite (M, n) */
  t: Frac;
  H: QV;
  /** |a x₀ + b y₀ + c z₀ + d| */
  num: number;
  /** distance exacte */
  dist: string;
  distValue: number;
}

/** projeté orthogonal de M sur le plan et distance |a x₀ + b y₀ + c z₀ + d| / ‖n‖ */
export function projection(P: Plane, M: IV): Projection {
  const val = idot(P.n, M) + planeD(P);
  const nn = n2(P.n);
  const t = frac(-val, nn);
  return {
    t,
    H: qadd(toQ(M), qscale(t, toQ(P.n))),
    num: Math.abs(val),
    dist: surdText(overSqrt(frac(Math.abs(val)), frac(nn))),
    distValue: Math.abs(val) / Math.sqrt(nn),
  };
}

export interface LineProjection {
  /** paramètre du projeté K sur la droite (B, u) */
  t: Frac;
  K: QV;
  /** vecteur MK */
  MK: QV;
  /** vecteur BM, produit vectoriel BM ∧ u */
  BM: IV;
  cross: IV;
  /** MK² exact */
  sq: Frac;
  dist: string;
  distValue: number;
}

/**
 * Projeté orthogonal K de M sur la droite (B, u) et distance MK : K = B + t u
 * avec MK · u = 0, soit t = BM · u / ‖u‖² ; MK² = ‖BM‖² − (BM · u)²/‖u‖²
 * = ‖BM ∧ u‖² / ‖u‖² (u ≠ 0).
 */
export function lineProjection(L: Line, M: IV): LineProjection {
  const BM = isub(M, L.B);
  const uu = n2(L.u);
  const t = frac(idot(BM, L.u), uu);
  const cross = icross(BM, L.u);
  const sq = frac(n2(cross), uu);
  const K = qadd(toQ(L.B), qscale(t, toQ(L.u)));
  return {
    t,
    K,
    MK: qadd(K, qscale(frac(-1), toQ(M))),
    BM,
    cross,
    sq,
    dist: surdText(sqrtFrac(sq)),
    distValue: Math.sqrt(n2(cross) / uu),
  };
}

/** segment de la droite dans le cube [−R ; R]³ (méthode des tranches) */
export function clipLine(B: readonly number[], u: readonly number[], R: number) {
  let lo = -Infinity;
  let hi = Infinity;
  for (let i = 0; i < 3; i++) {
    if (u[i] === 0) {
      if (Math.abs(B[i]!) > R) return null;
      continue;
    }
    const t1 = (-R - B[i]!) / u[i]!;
    const t2 = (R - B[i]!) / u[i]!;
    lo = Math.max(lo, Math.min(t1, t2));
    hi = Math.min(hi, Math.max(t1, t2));
  }
  return lo <= hi ? ([lo, hi] as const) : null;
}

/** « −4t + 8 », « t + 5 », « 2t », « 7 » ; `constFirst` : « 2 + d » */
export function affineText(a: number, b: number, v: string, constFirst = false): string {
  const term = a === 0 ? '' : `${a === -1 ? '−' : a === 1 ? '' : m(a)}${v}`;
  if (constFirst) {
    if (a === 0) return m(b);
    return b === 0
      ? term
      : `${m(b)} ${a < 0 ? '−' : '+'} ${Math.abs(a) === 1 ? '' : Math.abs(a)}${v}`;
  }
  if (!term) return m(b);
  return b === 0 ? term : `${term} ${b < 0 ? '−' : '+'} ${Math.abs(b)}`;
}

/* ---------- figure ---------- */

/** vue de départ : aucun vecteur de départ n'y est vu presque de face */
export const DEFAULT_VIEW = { yaw: Math.PI / 5, pitch: Math.PI / 7 };

/** valeurs de départ (exemples des cartes, choisis lisibles dans la vue de départ) */
export const DEFAULTS = {
  points: { A: [1, 2, 2] as IV, B: [3, 1, 4] as IV },
  vectors: { u: [1, 2, -1] as IV, v: [1, -1, 1] as IV, w: [3, 0, 1] as IV },
  plane: {
    n: [2, -1, 3] as IV,
    A: [1, 2, 0] as IV,
    B: [1, 0, 2] as IV,
    u: [0, 1, 1] as IV,
    M: [2, 3, 1] as IV,
  },
};

type R3 = readonly [number, number, number];
const fdot = (a: R3, b: R3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const fnorm = (a: R3) => Math.hypot(a[0], a[1], a[2]);

/** base orthonormée (e1, e2) d'un plan de vecteur normal n (n ≠ 0) */
export function planeBasis(n: R3): [R3, R3] {
  const nn = fnorm(n);
  const N: R3 = [n[0] / nn, n[1] / nn, n[2] / nn];
  const a: R3 = Math.abs(N[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const k = fdot(a, N);
  const r: R3 = [a[0] - k * N[0], a[1] - k * N[1], a[2] - k * N[2]];
  const rn = fnorm(r);
  const e1: R3 = [r[0] / rn, r[1] / rn, r[2] / rn];
  const e2: R3 = [
    N[1] * e1[2] - N[2] * e1[1],
    N[2] * e1[0] - N[0] * e1[2],
    N[0] * e1[1] - N[1] * e1[0],
  ];
  return [e1, e2];
}

/**
 * Carré du plan à dessiner : il contient les points `pts` (du plan) avec
 * une marge d'une unité, demi-côté au moins `min` et au plus `max`.
 * `c0` : un point du plan (le plus proche de O).
 */
export function planePatch(n: R3, c0: R3, pts: readonly R3[], min = 3, max = 7) {
  const [e1, e2] = planeBasis(n);
  const st = pts.map((p) => {
    const d: R3 = [p[0] - c0[0], p[1] - c0[1], p[2] - c0[2]];
    return [fdot(d, e1), fdot(d, e2)] as const;
  });
  const s = st.map((q) => q[0]);
  const t = st.map((q) => q[1]);
  const lo = [Math.min(0, ...s), Math.min(0, ...t)];
  const hi = [Math.max(0, ...s), Math.max(0, ...t)];
  const mid = [(lo[0]! + hi[0]!) / 2, (lo[1]! + hi[1]!) / 2] as const;
  const half = Math.min(max, Math.max(min, (hi[0]! - lo[0]!) / 2 + 1, (hi[1]! - lo[1]!) / 2 + 1));
  const center: R3 = [
    c0[0] + mid[0] * e1[0] + mid[1] * e2[0],
    c0[1] + mid[0] * e1[1] + mid[1] * e2[1],
    c0[2] + mid[0] * e1[2] + mid[1] * e2[2],
  ];
  return { center, e1, e2, half };
}

/** un point est-il dans le cube dessiné [−R ; R]³ ? */
export const inCube = (p: R3, R = 5) => p.every((c) => Math.abs(c) <= R + 1e-9);

/* ---------- défi ---------- */

export type QuizKind = 'length' | 'colinear' | 'plane' | 'intersect' | 'distance';
export const QUIZ_KINDS: QuizKind[] = ['length', 'colinear', 'plane', 'intersect', 'distance'];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}
export interface Quiz {
  kind: QuizKind;
  prompt: string;
  options: QuizOption[];
  explain: string;
}

type Rng = () => number;
const int = (lo: number, hi: number, rng: Rng) => lo + Math.floor(rng() * (hi - lo + 1));
const randVec = (rng: Rng, k = 3): IV => [int(-k, k, rng), int(-k, k, rng), int(-k, k, rng)];
function shuffle<T>(a: readonly T[], rng: Rng): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}
function finish(kind: QuizKind, prompt: string, cands: QuizOption[], explain: string, rng: Rng) {
  const seen = new Set<string>();
  const kept = cands.filter((c) => (seen.has(c.text) ? false : (seen.add(c.text), true)));
  if (kept.length < 4) return null;
  const [good, ...rest] = kept;
  return {
    kind,
    prompt,
    options: shuffle([good!, ...shuffle(rest, rng)].slice(0, 4), rng),
    explain,
  };
}

function quizLength(rng: Rng): Quiz | null {
  const A = randVec(rng);
  const B = randVec(rng);
  const AB = isub(B, A);
  if (AB.filter((c) => c !== 0).length < 2) return null;
  const sq = n2(AB);
  const absSum = AB.reduce((s, c) => s + Math.abs(c), 0);
  const wrongSq = n2(B) - n2(A);
  return finish(
    'length',
    `A${vecText(A)} et B${vecText(B)} dans un repère orthonormé. Que vaut AB ?`,
    [
      { text: rootText(sq), correct: true },
      { text: `${sq}`, correct: false, hint: `${sq} est AB² : il reste à prendre la racine.` },
      {
        text: rootText(absSum),
        correct: false,
        hint: 'On additionne les CARRÉS des coordonnées de AB, pas leurs valeurs absolues.',
      },
      ...(wrongSq > 0
        ? [
            {
              text: rootText(wrongSq),
              correct: false,
              hint: 'On élève au carré les DIFFÉRENCES de coordonnées (celles du vecteur AB), pas chaque coordonnée séparément.',
            },
          ]
        : []),
      {
        text: `${absSum}`,
        correct: false,
        hint: 'Ce n’est pas une somme de distances sur les axes : AB = √(…).',
      },
    ],
    `Vecteur AB${vecText(AB)} : ${lengthText(AB)}.`,
    rng,
  );
}

function quizColinear(rng: Rng): Quiz | null {
  const u = randVec(rng, 2);
  if (u.filter((c) => c !== 0).length < 2) return null;
  const k = [-3, -2, 2, 3, frac(1, 2), frac(-1, 2)][int(0, 5, rng)]!;
  const K = typeof k === 'number' ? frac(k) : k;
  const exact: QV = qscale(K, toQ(u));
  if (exact.some((c) => c.q !== 1)) return null;
  const v: IV = [exact[0].p, exact[1].p, exact[2].p];
  // une fois sur deux, une coordonnée faussée : plus colinéaires
  const broken = rng() < 0.5;
  const i = u.findIndex((c) => c !== 0);
  const j = [0, 1, 2].filter((x) => x !== i)[int(0, 1, rng)]!;
  const w: IV = broken ? (v.map((c, x) => (x === j ? c + 1 : c)) as unknown as IV) : v;
  const col = colinearity(u, w);
  const kText = fracText(K);
  const kv = (t: string) => `Oui : v = ${t === '1' ? '' : t === '−1' ? '−' : `${t} `}u`;
  return finish(
    'colinear',
    `u${vecText(u)} et v${vecText(w)}. Ces vecteurs sont-ils colinéaires ?`,
    [
      col.kind === 'yes'
        ? { text: kv(fracText(col.k)), correct: true }
        : { text: 'Non', correct: true },
      ...(col.kind === 'yes'
        ? [
            {
              text: 'Non',
              correct: false,
              hint: `Les trois coordonnées de v valent ${kText} fois celles de u.`,
            },
            {
              text: kv(fracText(div(frac(1), K))),
              correct: false,
              hint: 'Rapport inversé : on cherche k avec v = k u, donc k = (coordonnée de v)/(coordonnée de u).',
            },
            {
              text: kv(fracText(mul(K, frac(-1)))),
              correct: false,
              hint: 'Erreur de signe sur k.',
            },
          ]
        : [
            {
              text: kv(kText),
              correct: false,
              hint: `Il faut vérifier les TROIS coordonnées : ${col.kind === 'no' ? col.why : ''}.`,
            },
            {
              text: kv(fracText(div(frac(1), K))),
              correct: false,
              hint: 'Dans l’espace, la proportionnalité doit tenir sur les trois coordonnées.',
            },
            {
              text: 'On ne peut pas savoir sans figure',
              correct: false,
              hint: 'Les coordonnées suffisent : v = k u se teste coordonnée par coordonnée.',
            },
          ]),
    ],
    col.kind === 'yes'
      ? `${vecText(w)} = ${kText} × ${vecText(u)} : v = ${kText} u, les vecteurs sont colinéaires.`
      : `Pas de k commun : ${col.kind === 'no' ? col.why : ''} — les vecteurs ne sont pas colinéaires.`,
    rng,
  );
}

function quizPlane(rng: Rng): Quiz | null {
  const n = randVec(rng, 3);
  const A = randVec(rng, 3);
  if (n.filter((c) => c !== 0).length < 2) return null;
  const P = { n, A };
  const d = planeD(P);
  if (d === 0) return null;
  const good = planeText(P);
  const flip = planeText({ n, A: A.map((c) => -c) as unknown as IV });
  const swap = planeText({ n: A, A: n });
  const noD = planeText({ n, A: [0, 0, 0] });
  return finish(
    'plane',
    `Équation cartésienne du plan passant par A${vecText(A)} de vecteur normal n${vecText(n)} ?`,
    [
      { text: good, correct: true },
      {
        text: flip,
        correct: false,
        hint: `Signe de d : A doit vérifier l’équation, donc d = ${m(d)}.`,
      },
      {
        text: swap,
        correct: false,
        hint: 'Les coefficients de x, y, z sont les coordonnées du vecteur NORMAL, pas celles de A.',
      },
      {
        text: noD,
        correct: false,
        hint: 'Ce plan passe par O ; il faut trouver d avec le point A.',
      },
    ],
    `n${vecText(n)} donne ${planeText({ n, A: [0, 0, 0] }).replace(' = 0', ' + d = 0')} ; A dans le plan : ${affineText(1, idot(n, A), 'd', true)} = 0, donc d = ${m(d)} : ${good}.`,
    rng,
  );
}

const qptText = (name: string, v: QV) => `${name}${vecText(v)}`;

function quizIntersect(rng: Rng): Quiz | null {
  const n = randVec(rng, 2);
  const A = randVec(rng, 2);
  const B = randVec(rng, 3);
  const u = randVec(rng, 2);
  if (isNull(n) || isNull(u)) return null;
  const P = { n, A };
  const L = { B, u };
  const pos = position(P, L);
  if (pos.kind !== 'secante' || pos.t.q !== 1 || isZero(pos.t)) return null;
  const H = pos.H;
  const tWrong = mul(pos.t, frac(-1));
  const Hw = qadd(toQ(B), qscale(tWrong, toQ(u)));
  const Hn = qadd(toQ(B), qscale(pos.t, toQ(n)));
  return finish(
    'intersect',
    `𝒟 : ${paramLines(L).join(', ')} (t ∈ ℝ) et 𝒫 : ${planeText(P)}. En quel point 𝒟 coupe-t-elle 𝒫 ?`,
    [
      { text: qptText('', H), correct: true },
      {
        text: qptText('', toQ(B)),
        correct: false,
        hint: 'C’est le point de paramètre t = 0, qui n’est pas sur le plan.',
      },
      {
        text: qptText('', Hw),
        correct: false,
        hint: 'Erreur de signe en résolvant l’équation en t.',
      },
      {
        text: qptText('', Hn),
        correct: false,
        hint: 'On remplace x, y, z par les expressions de la DROITE 𝒟, pas par les coordonnées du vecteur normal.',
      },
    ],
    `On remplace x, y, z par leurs expressions en t dans l’équation de 𝒫 : ${affineText(idot(n, u), idot(n, B) + planeD(P), 't')} = 0, donc t = ${fracText(pos.t)} et le point ${qptText('H', H)}.`,
    rng,
  );
}

function quizDistance(rng: Rng, prepa: boolean): Quiz | null {
  const n = randVec(rng, 2);
  if (n.filter((c) => c !== 0).length < 2) return null;
  const A = randVec(rng, 2);
  const M = randVec(rng, 3);
  const P = { n, A };
  const pr = projection(P, M);
  if (pr.num === 0) return null;
  const nn = n2(n);
  const val = idot(n, M) + planeD(P);
  const withoutD = Math.abs(idot(n, M));
  const norm = rootText(nn);
  const tle = `On suit la droite passant par M dirigée par n : ${paramLines({ B: M, u: n }).join(', ')} ; l’équation de 𝒫 donne ${affineText(nn, val, 't')} = 0, soit t = ${fracText(pr.t)}, d’où H${vecText(pr.H)} et MH = |t| × ‖n‖ = ${fracText(frac(pr.num, nn))} × ${norm} = ${pr.dist}.`;
  const formula = `d(M, 𝒫) = |a x₀ + b y₀ + c z₀ + d| / √(a² + b² + c²) = |${m(val)}| / √${nn} = ${pr.dist}.`;
  return finish(
    'distance',
    `Distance du point M${vecText(M)} au plan 𝒫 : ${planeText(P)} ?`,
    [
      { text: pr.dist, correct: true },
      {
        text: `${pr.num}`,
        correct: false,
        hint: prepa
          ? `Il faut diviser |a x₀ + b y₀ + c z₀ + d| par ‖n‖ = ${norm}.`
          : `MH = |t| × ‖n‖, avec t = ${fracText(pr.t)} et ‖n‖ = ${norm} : ce n’est pas ${pr.num}.`,
      },
      {
        text: fracText(frac(pr.num, nn)),
        correct: false,
        hint: prepa
          ? `On divise par ‖n‖ = √(a² + b² + c²), pas par a² + b² + c² = ${nn}.`
          : `C’est |t| ; il faut encore multiplier par ‖n‖ = ${norm}.`,
      },
      ...(withoutD !== pr.num
        ? [
            {
              text: projection({ n, A: [0, 0, 0] }, M).dist,
              correct: false,
              hint: prepa
                ? 'Le terme d de l’équation compte : a x₀ + b y₀ + c z₀ + d.'
                : 'Le terme constant de l’équation de 𝒫 compte quand on cherche t.',
            },
          ]
        : []),
      {
        text: '0',
        correct: false,
        hint: 'M n’est pas sur le plan : son équation n’est pas vérifiée.',
      },
    ],
    prepa ? `${formula} (Méthode de Terminale : ${tle})` : tle,
    rng,
  );
}

const MAKERS: Record<QuizKind, (rng: Rng, prepa: boolean) => Quiz | null> = {
  length: quizLength,
  colinear: quizColinear,
  plane: quizPlane,
  intersect: quizIntersect,
  distance: quizDistance,
};

/** `prepa` : la distance au plan s'explique aussi par la formule |ax₀ + by₀ + cz₀ + d| / ‖n‖ */
export function makeQuiz(kind: QuizKind, rng: Rng, prepa = false): Quiz {
  for (let i = 0; i < 2000; i++) {
    const q = MAKERS[kind](rng, prepa);
    if (q) return q;
  }
  throw new Error(`défi ${kind} introuvable`);
}
